'use client';

import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslations } from 'next-intl';
import { Copy, Check } from 'lucide-react';
import { customAlphabet } from 'nanoid';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

interface ShareInterface {
  shareEnabled?: boolean;
  shareToken?: string | null;
  shareFrom?: string | null;
  shareTo?: string | null;
  sharePublishedAt?: Date | null;
}

interface ShareInputInterface {
  shareEnabled?: boolean;
  shareToken?: string;
  shareFrom?: string;
  shareTo?: string;
  sharePublishedAt?: Date;
}

interface BaseShareDialogProps {
  item: ShareInterface;
  urlPrefix?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  update: (props: ShareInputInterface) => void;
}

export const BaseShareDialog = ({
  item,
  urlPrefix,
  update,
  open,
  setOpen,
}: BaseShareDialogProps) => {
  const [shareLink, setShareLink] = useState('');
  const [showMoreSettings, setShowMoreSettings] = useState(false);
  const [copied, setCopied] = useState(false);
  const t = useTranslations('calendar');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (item) {
      setShareLink(
        `${window.location.origin}/${urlPrefix ? `${urlPrefix}/` : ''}${item.shareToken}`,
      );
    }
  }, [item, urlPrefix]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error(t('dialog.share.copyFailed'), error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="flex flex-col gap-0 p-0 sm:max-w-xl [&>button:last-child]:top-4"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="text-xl font-semibold">{t('dialog.share.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">{t('dialog.share.public')}</Label>
                <Switch
                  checked={item?.shareEnabled}
                  onCheckedChange={async () => {
                    const nanoid = customAlphabet(
                      '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
                      10,
                    );

                    update({
                      shareEnabled: !item?.shareEnabled,
                      shareToken: item?.shareToken ? item.shareToken : nanoid(),
                      shareFrom: item?.shareFrom ?? 'from-start',
                      shareTo: item?.shareTo ?? 'all-time',
                      sharePublishedAt: item?.sharePublishedAt ?? new Date(),
                    });
                  }}
                  className="h-5 w-9"
                />
              </div>
              <p className="text-sm text-gray-500">{t('dialog.share.publicDescription')}</p>
            </div>

            {item?.shareEnabled && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="flex-1 bg-gray-50 text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="px-3"
                    disabled={copied}
                  >
                    {copied ? (
                      <>
                        <Check className="mr-1 h-4 w-4" />
                        {t('dialog.share.linkCopied')}
                      </>
                    ) : (
                      <>
                        <Copy className="mr-1 h-4 w-4" />
                        {t('dialog.share.copy')}
                      </>
                    )}
                  </Button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={updating}
                      onClick={async () => {
                        setUpdating(true);
                        try {
                          await update({
                            sharePublishedAt: new Date(),
                          });
                        } catch {}
                        setUpdating(false);
                      }}
                    >
                      {t('dialog.share.publish')}
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setShowMoreSettings(!showMoreSettings)}
                    >
                      {t('dialog.share.settings')}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-xs text-gray-500">
                      {t('dialog.share.lastPublished')}{' '}
                      {item.sharePublishedAt
                        ? format(item.sharePublishedAt, 'PPp', { locale: sv })
                        : ''}
                    </span>
                  </div>
                </div>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    showMoreSettings ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="space-y-4 border-t pt-4">
                    <div>
                      <h4 className="mb-2 text-sm font-medium">
                        {t('dialog.share.shareSettings')}
                      </h4>
                      <p className="mb-4 text-sm text-gray-500">
                        {t('dialog.share.shareSettingsDescription')}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="from-period" className="text-sm font-medium">
                          {t('dialog.share.from')}
                        </Label>
                        <Select
                          value={item.shareFrom!}
                          onValueChange={async (value) => {
                            await update({
                              shareFrom: value,
                            });
                          }}
                        >
                          <SelectTrigger id="from-period" className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="from-start">
                              {t('dialog.share.fromStart')}
                            </SelectItem>
                            <SelectItem value="today">{t('dialog.share.today')}</SelectItem>
                            <SelectItem value="2-weeks-back">
                              {t('dialog.share.twoWeeksBack')}
                            </SelectItem>
                            <SelectItem value="4-weeks-back">
                              {t('dialog.share.fourWeeksBack')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="to-period" className="text-sm font-medium">
                          {t('dialog.share.to')}
                        </Label>
                        <Select
                          value={item.shareTo!}
                          onValueChange={async (value) => {
                            await update({
                              shareTo: value,
                            });
                          }}
                        >
                          <SelectTrigger id="to-period" className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all-time">{t('dialog.share.toEnd')}</SelectItem>
                            <SelectItem value="2-weeks">{t('dialog.share.twoWeeks')}</SelectItem>
                            <SelectItem value="4-weeks">{t('dialog.share.fourWeeks')}</SelectItem>
                            <SelectItem value="6-weeks">{t('dialog.share.sixWeeks')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
